import React from "react";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../lib/api";
import { ProductImage } from "./ProductImage";

export const CartDrawer = () => {
  const { items, open, setOpen, removeItem, updateQty, subtotal } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/70 z-40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="cart-overlay"
        />
      )}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-[#121824] border-l border-subtle z-50 transform transition-transform duration-300 flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-5 border-b border-subtle">
          <div>
            <p className="eyebrow">Votre commande</p>
            <h3 className="font-display text-2xl text-white">Panier</h3>
          </div>
          <button
            onClick={() => setOpen(false)}
            data-testid="cart-close-btn"
            className="btn-ghost p-2"
            aria-label="Fermer"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-4 py-16">
              <ShoppingBag size={48} className="text-gray-600" />
              <p className="text-gray-400">Votre panier est vide.</p>
              <button onClick={() => setOpen(false)} className="btn-outline-gold mt-2" data-testid="cart-empty-shop-btn">
                Découvrir le catalogue
              </button>
            </div>
          ) : (
            <ul className="flex flex-col gap-4">
              {items.map((it) => (
                <li
                  key={it.product_id}
                  className="flex gap-3 border-b border-white/5 pb-4"
                  data-testid={`cart-item-${it.product_id}`}
                >
                  <ProductImage src={it.image_url} alt={it.name} testId={`cart-product-image-${it.product_id}`}
                    className="w-20 h-20 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-base text-white leading-tight truncate">{it.name}</p>
                    <p className="text-gold text-sm mt-1">{formatPrice(it.price)}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center border border-white/15">
                        <button
                          onClick={() => updateQty(it.product_id, it.quantity - 1)}
                          className="p-1.5 hover:bg-white/5 transition"
                          data-testid={`cart-decrease-${it.product_id}`}
                          aria-label="Réduire"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="px-3 text-sm" data-testid={`cart-qty-${it.product_id}`}>{it.quantity}</span>
                        <button
                          onClick={() => updateQty(it.product_id, it.quantity + 1)}
                          className="p-1.5 hover:bg-white/5 transition"
                          data-testid={`cart-increase-${it.product_id}`}
                          aria-label="Augmenter"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(it.product_id)}
                        className="text-gray-500 hover:text-red-400 transition"
                        data-testid={`cart-remove-${it.product_id}`}
                        aria-label="Retirer"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-white whitespace-nowrap">
                    {formatPrice(it.price * it.quantity)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-subtle p-5 bg-[#0a0f1a]">
            <div className="flex justify-between mb-2 text-sm text-gray-300">
              <span>Sous-total</span>
              <span data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
            </div>
            <div className="flex justify-between mb-4 text-xs text-gray-500">
              <span>Livraison</span>
              <span>Calculée à l'étape suivante</span>
            </div>
            <button
              onClick={handleCheckout}
              data-testid="cart-checkout-btn"
              className="btn-gold w-full"
            >
              Commander maintenant
            </button>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 text-center mt-3">
              Espèces, carte bancaire ou lien de paiement après confirmation
            </p>
          </div>
        )}
      </aside>
    </>
  );
};
